import { createFileRoute, useParams, useSearch, useNavigate } from '@tanstack/react-router'
import { useAssets, useAssetTypes, useAssetCount } from '../../../api/queries'
import { Button } from '../../../components/ui/button'
import { Link } from '@tanstack/react-router'
import { mdiPlus, mdiRefresh, mdiDomain, mdiMagnify, mdiAlert } from '@mdi/js'
import { AppIcon } from '../../../components/ui/icon'
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs'
import { PageHeader } from '../../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../../lib/section-icons'
import { AssetTypeTile } from '../../../components/assets/AssetTypeTile'
import { EmptyState } from '../../../components/ui/empty-state'

export const Route = createFileRoute('/$org/assets/old')({
    component: RouteComponent,
    validateSearch: (search: Record<string, unknown>) => {
        return {
            showBuildingGroups: search.showBuildingGroups === false || search.showBuildingGroups === 'false' ? false : true,
            showBuildings: search.showBuildings === false || search.showBuildings === 'false' ? false : true,
            q: (search.q as string) || '',
            asset_type_uid: search.asset_type_uid as string | undefined,
            flag: search.flag as string | undefined,
        }
    },
})

function RouteComponent() {
    const { org } = useParams({ from: '/$org/assets/old' })
    const { showBuildingGroups, showBuildings, q, asset_type_uid, flag } = useSearch({ from: '/$org/assets/old' })
    const navigate = useNavigate({ from: '/$org/assets/old' })

    // Set up breadcrumbs
    useBreadcrumbs([
        { title: 'Home', path: `/${org}` },
        { title: 'Assets' }
    ])

    // Use React Query hooks
    const {
        data: assetTypes = [],
        isLoading: assetTypesLoading,
        error: assetTypesError
    } = useAssetTypes(org)


    // Get UIDs for building_group and building types
    const buildingGroupTypeUid = assetTypes.find(t => t.code === 'building_group')?.uid
    const buildingTypeUid = assetTypes.find(t => t.code === 'building')?.uid

    // Determine which asset types to fetch based on URL parameter
    const assetTypeUids = asset_type_uid
        ? [asset_type_uid] // If asset_type_uid is provided, only fetch that specific type
        : [buildingGroupTypeUid, buildingTypeUid].filter(Boolean) as string[] // Otherwise, fetch both building types

    const {
        data: allAssets = [],
        isLoading: assetsLoading,
        error: assetsError,
        refetch: refetchAssets
    } = useAssets(org, assetTypeUids, q, undefined, 'name', ['flags'], flag)

    // Fetch incomplete asset counts
    const { data: incompleteBuildingGroupCount = 0 } = useAssetCount(org, buildingGroupTypeUid, 'incomplete')
    const { data: incompleteBuildingCount = 0 } = useAssetCount(org, buildingTypeUid, 'incomplete')

    const loading = assetTypesLoading || assetsLoading
    const error = assetTypesError || assetsError

    // Filter assets based on selected types or specific asset_type_uid parameter
    const filteredAssets = allAssets.filter(asset => {
        // If asset_type_uid is provided via URL, show all assets of that type (ignore checkboxes)
        if (asset_type_uid) {
            return asset.asset_type_uid === asset_type_uid
        }
        
        // Otherwise, use the checkbox filters for building groups and buildings
        if (asset.asset_type_uid === buildingGroupTypeUid && showBuildingGroups) return true
        if (asset.asset_type_uid === buildingTypeUid && showBuildings) return true
        return false
    })

    // Update URL search params for filters
    const updateSearch = (updates: Partial<{ showBuildingGroups: boolean; showBuildings: boolean; q: string; asset_type_uid: string | undefined; flag: string | undefined }>) => {
        console.log('updateSearch called with:', updates)
        const currentSearch = { showBuildingGroups, showBuildings, q, asset_type_uid, flag }
        const newSearch = { ...currentSearch, ...updates }
        console.log('Current search:', currentSearch)
        console.log('New search:', newSearch)
        navigate({
            search: newSearch,
        })
    }

    // Allow unchecking both checkboxes (will show no assets if both unchecked)
    const handleBuildingGroupChange = (checked: boolean) => {
        console.log('Building group changed to:', checked)
        updateSearch({ showBuildingGroups: checked })
    }

    const handleBuildingChange = (checked: boolean) => {
        console.log('Building changed to:', checked)
        updateSearch({ showBuildings: checked })
    }

    const handleSearchChange = (searchValue: string) => {
        updateSearch({ q: searchValue })
    }

    const getAssetTypeName = (asset_type_uid: string) => {
        const assetTypeObj = assetTypes.find(t => t.uid === asset_type_uid)
        if (!assetTypeObj) {
            return 'Unknown Type'
        }
        if (typeof assetTypeObj.name === 'string') {
            return assetTypeObj.name
        }
        return assetTypeObj.name['en'] || assetTypeObj.name['fr'] || assetTypeObj.code
    }

    const getAssetTypeForAsset = (asset_type_uid: string) => {
        return assetTypes.find(t => t.uid === asset_type_uid)
    }

    const handleRefresh = () => {
        refetchAssets()
    }

    // Calculate total incomplete assets
    const totalIncompleteAssets = incompleteBuildingGroupCount + incompleteBuildingCount

    const handleShowIncomplete = () => {
        updateSearch({ flag: 'incomplete' })
    }

    return (
        <div className="space-y-6">
            <PageHeader
                icon={SECTION_ICONS.assets}
                title="Your assets"
                titleAside={
                    /* Warning icon for incomplete assets */
                    !flag && totalIncompleteAssets > 0 ? (
                        <button
                            onClick={handleShowIncomplete}
                            className="flex items-center gap-1 text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 transition-colors cursor-pointer"
                            title="Click to show incomplete assets"
                        >
                            <AppIcon path={mdiAlert} size="md" />
                            <span className="text-sm font-medium">
                                {totalIncompleteAssets} incomplete
                            </span>
                        </button>
                    ) : null
                }
                actions={
                <>
                    <Link
                        to="/$org/createBuildingGroup"
                        params={{ org }}
                        search={{}}
                    >
                        <Button className="bg-primary hover:bg-primary/90">
                            <AppIcon path={mdiPlus} size="md" className="mr-1" />
                            Create Assets
                        </Button>
                    </Link>
                    <Link
                        to="/$org/assets/createBasic"
                        params={{ org }}
                        search={{ parent_asset_uid: undefined, asset_type_uid: undefined, restrict_to_uids: undefined }}
                    >
                        <Button variant="outline">
                            <AppIcon path={mdiPlus} size="md" className="mr-1" />
                            Create Asset (Legacy)
                        </Button>
                    </Link>
                    <Button onClick={handleRefresh} variant="outline" disabled={loading}>
                        <AppIcon path={mdiRefresh} size="md" className="mr-1" />
                        {loading ? 'Loading...' : 'Refresh'}
                    </Button>
                </>
                }
            />

            {/* Incomplete Assets Summary */}
            {!asset_type_uid && (incompleteBuildingGroupCount > 0 || incompleteBuildingCount > 0) && (
                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-orange-800 dark:text-orange-200 mb-2">Incomplete Assets</h3>
                    <div className="flex gap-4 text-sm text-orange-700 dark:text-orange-300">
                        {incompleteBuildingGroupCount > 0 && (
                            <button
                                onClick={() => updateSearch({ asset_type_uid: buildingGroupTypeUid, flag: 'incomplete' })}
                                className="flex items-center gap-2 hover:text-orange-900 dark:hover:text-orange-100 transition-colors cursor-pointer"
                            >
                                <AppIcon path={mdiDomain} size="sm" />
                                <span>{incompleteBuildingGroupCount} incomplete building group{incompleteBuildingGroupCount !== 1 ? 's' : ''}</span>
                            </button>
                        )}
                        {incompleteBuildingCount > 0 && (
                            <button
                                onClick={() => updateSearch({ asset_type_uid: buildingTypeUid, flag: 'incomplete' })}
                                className="flex items-center gap-2 hover:text-orange-900 dark:hover:text-orange-100 transition-colors cursor-pointer"
                            >
                                <AppIcon path={SECTION_ICONS.assets} size="sm" />
                                <span>{incompleteBuildingCount} incomplete building{incompleteBuildingCount !== 1 ? 's' : ''}</span>
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Filter Controls */}
            <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex gap-4 items-center">
                    {/* Freesearch Input */}
                    <div className="flex items-center border border-border rounded-md px-3 py-2 bg-background min-w-64">
                        <AppIcon tone="muted" path={mdiMagnify} size="sm" className="mr-2" />
                        <input
                            type="text"
                            placeholder="Search assets..."
                            value={q}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            className="bg-transparent border-none outline-none text-sm flex-1 text-foreground placeholder:text-muted-foreground"
                        />
                    </div>

                    {/* Type Filters - only show if no specific asset_type_uid is provided */}
                    {!asset_type_uid && (
                        <>
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={showBuildingGroups}
                                    onChange={(e) => handleBuildingGroupChange(e.target.checked)}
                                    className="rounded border-border text-primary focus:ring-ring"
                                />
                                <span className="ml-2 flex items-center text-sm text-foreground">
                                    <AppIcon path={mdiDomain} size="sm" className="mr-1" />
                                    Building Groups
                                </span>
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={showBuildings}
                                    onChange={(e) => handleBuildingChange(e.target.checked)}
                                    className="rounded border-border text-primary focus:ring-ring"
                                />
                                <span className="ml-2 flex items-center text-sm text-foreground">
                                    <AppIcon path={SECTION_ICONS.assets} size="sm" className="mr-1" />
                                    Buildings
                                </span>
                            </label>
                        </>
                    )}
                    
                    {/* Show which asset type is being filtered when asset_type_uid is provided */}
                    {asset_type_uid && (
                        <div className="flex items-center text-sm text-muted-foreground bg-muted/50 px-3 py-1 rounded-md">
                            <AppIcon path={SECTION_ICONS.assets} size="sm" className="mr-2" />
                            Filtered by: {getAssetTypeName(asset_type_uid)}
                        </div>
                    )}
                    
                    {/* Show when incomplete flag is active */}
                    {flag === 'incomplete' && (
                        <div className="flex items-center text-sm text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/20 px-3 py-1 rounded-md border border-orange-200 dark:border-orange-800">
                            <AppIcon path={mdiAlert} size="sm" className="mr-2" />
                            Showing incomplete assets only
                            <button
                                onClick={() => updateSearch({ flag: undefined })}
                                className="ml-2 text-orange-600 hover:text-orange-800 dark:text-orange-400 dark:hover:text-orange-200 text-xs underline"
                                title="Clear incomplete filter"
                            >
                                Clear
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
                    <div className="text-destructive text-sm">
                        {(error as any)?.title || (error as any)?.message || 'An error occurred'}
                    </div>
                </div>
            )}

            {/* Assets Table */}
            <div className="bg-card rounded-lg border border-border overflow-x-auto">
                <table className="min-w-full divide-y divide-border">
                    <thead className="bg-muted/50">
                        <tr>
                            <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                                Name
                            </th>
                            <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                                Type
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-card divide-y divide-border">
                        {loading ? (
                            <tr>
                                <td colSpan={2} className="px-6 py-4 text-center text-muted-foreground">
                                    Loading assets...
                                </td>
                            </tr>
                        ) : filteredAssets.length === 0 ? (
                            <tr>
                                <td colSpan={2} className="px-6">
                                    <EmptyState variant="inline" live title="No assets match the selected filters" />
                                </td>
                            </tr>
                        ) : (
                            filteredAssets.map((asset) => {
                                const assetType = getAssetTypeForAsset(asset.asset_type_uid)

                                return (
                                    <tr key={asset.uid} className="hover:bg-muted/50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                                            <div className="flex items-center gap-2">
                                                <AssetTypeTile
                                                    type={assetType}
                                                    size="sm"
                                                    title={`${assetType?.code || 'unknown'} asset`}
                                                />
                                                <Link
                                                    to="/$org/assets/$assetUid"
                                                    params={{ org, assetUid: asset.uid }}
                                                    className="text-primary hover:text-primary/80 hover:underline"
                                                >
                                                    {asset.name}
                                                </Link>
                                                {/* Wrench emoji for incomplete building groups and buildings */}
                                                {assetType?.code === 'building_group' && asset.flags?.includes('incomplete') && (
                                                    <Link
                                                        to="/$org/createBuildingGroup"
                                                        params={{ org }}
                                                        search={{ asset_uid: asset.uid }}
                                                        className="text-lg hover:scale-110 transition-transform"
                                                        title="Configure incomplete building group"
                                                    >
                                                        🔧
                                                    </Link>
                                                )}
                                                {assetType?.code === 'building' && asset.flags?.includes('incomplete') && (
                                                    <Link
                                                        to="/$org/createBuilding"
                                                        params={{ org }}
                                                        search={{ asset_uid: asset.uid }}
                                                        className="text-lg hover:scale-110 transition-transform"
                                                        title="Configure incomplete building"
                                                    >
                                                        🔧
                                                    </Link>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                                            {getAssetTypeName(asset.asset_type_uid)}
                                        </td>
                                    </tr>
                                )
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Assets Count */}
            <div className="text-sm text-muted-foreground">
                {filteredAssets.length > 0 && (
                    <p>Showing {filteredAssets.length} of {allAssets.length} asset{allAssets.length !== 1 ? 's' : ''}</p>
                )}
            </div>
        </div>
    )
}
